import {
  useState,
  type ComponentProps,
  type ComponentType,
  type FormEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  CircleAlert,
  KeyRound,
  LoaderCircle,
  Mail,
  User,
} from 'lucide-react';
import { useErrorText } from '@/lib/error-text';
import { useLogin, useRegister } from '@/resources/auth/auth.service';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function IconInput({
  icon: Icon,
  className,
  ...props
}: ComponentProps<typeof Input> & { icon: ComponentType<{ className?: string }> }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input className={`pl-8 ${className ?? ''}`} {...props} />
    </div>
  );
}

export function AuthPanel({
  submitLabel,
  onAuthenticated,
}: {
  submitLabel?: { login: string; register: string };
  onAuthenticated?: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const loginMutation = useLogin();
  const registerMutation = useRegister();
  const isRegister = mode === 'register';
  const active = isRegister ? registerMutation : loginMutation;

  function switchMode(next: 'login' | 'register') {
    setMode(next);
    setPassword('');
    loginMutation.reset();
    registerMutation.reset();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const done = { onSuccess: () => onAuthenticated?.() };
    if (isRegister) {
      registerMutation.mutate({ email, password, name: name.trim() || undefined }, done);
    } else {
      loginMutation.mutate({ email, password }, done);
    }
  }

  const invalid = Boolean(active.error);
  const label = submitLabel ?? {
    login: t('auth.submitLogin'),
    register: t('auth.submitRegister'),
  };

  return (
    <Tabs value={mode} onValueChange={(v) => switchMode(v as 'login' | 'register')}>
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="login">{t('auth.tabLogin')}</TabsTrigger>
        <TabsTrigger value="register">{t('auth.tabRegister')}</TabsTrigger>
      </TabsList>

      <form onSubmit={onSubmit} className="mt-6">
        <FieldGroup>
          <TabsContent value="register" className="mt-0">
            <Field>
              <FieldLabel htmlFor="auth-name">{t('auth.nameLabel')}</FieldLabel>
              <IconInput
                icon={User}
                id="auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth.namePlaceholder')}
                autoComplete="name"
              />
            </Field>
          </TabsContent>

          <Field data-invalid={invalid}>
            <FieldLabel htmlFor="auth-email">{t('auth.emailLabel')}</FieldLabel>
            <IconInput
              icon={Mail}
              id="auth-email"
              type="email"
              required
              aria-invalid={invalid}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
            />
          </Field>

          <Field data-invalid={invalid}>
            <FieldLabel htmlFor="auth-password">{t('auth.passwordLabel')}</FieldLabel>
            <IconInput
              icon={KeyRound}
              id="auth-password"
              type="password"
              required
              aria-invalid={invalid}
              minLength={isRegister ? 8 : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
            {isRegister && <FieldDescription>{t('auth.passwordHint')}</FieldDescription>}
            {active.error && (
              <FieldError className="flex items-center gap-1.5">
                <CircleAlert className="size-4 shrink-0" />
                {errorText(active.error)}
              </FieldError>
            )}
          </Field>

          <Field>
            <Button type="submit" disabled={active.isPending}>
              {active.isPending ? (
                <>
                  <LoaderCircle className="size-4 animate-spin" />
                  {isRegister ? t('auth.pendingRegister') : t('auth.pendingLogin')}
                </>
              ) : (
                <>
                  {isRegister ? label.register : label.login}
                  <ArrowRight className="size-4" />
                </>
              )}
            </Button>
          </Field>
        </FieldGroup>
      </form>
    </Tabs>
  );
}
